/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, merge, of, EMPTY } from 'rxjs';
import { map, scan, distinctUntilChanged, switchMap, mapTo, catchError } from 'rxjs/operators';
import { ProjectCatalogType } from '@touchplan/fancy-referencer';
import { FancyFirebaseState } from '../common/fancy-firebase/services/fancy-firebase-state.service';

import { RouteUtils } from './route-utils.service';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';

//just replicate the old one, which was meant to solve the case of
//"selected projects not necessarily being tied to the state"
//in theory we should be able to edit this out when everything is all angular

interface ProjectParamState {
	projectId?: string;
	planId?: string;
}

@Injectable()
export class ProjectNotifier {
	private storageSubject = new BehaviorSubject<ProjectParamState>({});

	//this is the main one
	public projectState: Observable<ProjectParamState>;
	//this is the less useful one
	public nonDistinctProjectState: Observable<ProjectParamState>;

	public projectStateSnapshot: ProjectParamState;

	private logger: ILoggerService = LoggerServiceFactory.create('ProjectNotifier');

	constructor(routeUtils: RouteUtils, private firebaseState: FancyFirebaseState) {
		routeUtils.snapshot
			.pipe(
				map((snapshot) => {
					const map: ProjectParamState = {};
					if (!snapshot) {
						return map;
					}

					//angular router resolved from the bottom up, as opposed to the angularjs router which went top down. So... get the child
					const childRoute = routeUtils.getMostChildish(snapshot);
					map.projectId = childRoute.paramMap.get('projectId') || null;
					map.planId = childRoute.paramMap.get('planId') || null;
					return map;
				})
			)
			.subscribe(this.storageSubject);

		const temp = this.storageSubject.pipe(
			scan((acc: ProjectParamState, state: ProjectParamState) => {
				const newObj: ProjectParamState = Object.assign({}, acc);
				['projectId', 'planId'].forEach((key: keyof ProjectParamState) => {
					if (state[key] !== undefined) {
						newObj[key] = state[key];
					}
				});
				return newObj;
			}, {} as ProjectParamState),
			switchMap((state) => {
				if (!state?.projectId) {
					return of(state);
				}
				//_may_ need to have this start with null in case of delays. Unsure if it's fine for this flow
				// projectId -> waiting with invalid projectId... -> projectId
				// might need projectId -> null -> projectId
				// of course... don't think there's any way currently to switch from project to project without unselecting in the ui
				return this.firebaseState.getProjectCatalog$(state.projectId, ProjectCatalogType.ProjectData).pipe(
					catchError((err) => {
						this.logger.warn(err);
						return EMPTY;
					}),
					mapTo(state)
				);
			})
		);

		this.projectState = temp.pipe(
			distinctUntilChanged((a, b) => a.projectId === b.projectId && a.planId === b.planId)
		);
		this.nonDistinctProjectState = temp;

		this.projectState.subscribe((sync) => (this.projectStateSnapshot = sync));
	}

	public setProjectAndPlan(projectId?: string, planId?: string) {
		const obj: ProjectParamState = {};
		if (projectId !== undefined) {
			obj.projectId = projectId;
		}
		if (planId !== undefined) {
			obj.planId = planId;
		}
		this.storageSubject.next(obj);
	}

	public setProject(projectId: string) {
		this.setProjectAndPlan(projectId);
	}
	public setPlan(planId: string) {
		this.setProjectAndPlan(undefined, planId);
	}
}
